import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Badge from "../../components/common/Badge";
import ActionButtons from "../../components/common/ActionButtons";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import {
  useGetKiosksQuery,
  useCreateKioskMutation,
  useUpdateKioskMutation,
  useDeleteKioskMutation,
  // useLockKioskMutation,
  // useUnlockKioskMutation,
} from "../../store/api";

const Kiosks = () => {
  const { data, isLoading, isError, error } = useGetKiosksQuery();
  const { features } = useStoreFeatures(true);
  const [createKiosk, createState] = useCreateKioskMutation();
  const [updateKiosk, updateState] = useUpdateKioskMutation();
  const [deleteKiosk] = useDeleteKioskMutation();
  // const [lockKiosk] = useLockKioskMutation();
  // const [unlockKiosk] = useUnlockKioskMutation();

  const [form, setForm] = useState({
    deviceName: "",
    deviceKey: "",
    pairingCode: "",
    bridgeBaseUrl: "",
    signalrUrl: "",
    inactivitySeconds: 60,
    paymentsEnabled: true,
    printingEnabled: true,
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    kioskId: null,
    kioskName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const kiosks = Array.isArray(data) ? data : [];
  const maxKiosks =
    features && features.maxKiosks !== undefined ? features.maxKiosks : null;
  const isLimitReached =
    maxKiosks !== null && Number.isFinite(maxKiosks)
      ? kiosks.length >= maxKiosks
      : false;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        deviceName: form.deviceName,
        deviceKey: form.deviceKey,
        pairingCode: form.pairingCode,
        bridgeBaseUrl: form.bridgeBaseUrl || null,
        signalrUrl: form.signalrUrl || null,
        inactivitySeconds: parseInt(form.inactivitySeconds) || 60,
        paymentsEnabled: form.paymentsEnabled,
        printingEnabled: form.printingEnabled,
        isActive: form.isActive,
      };
      if (editingId) {
        await updateKiosk({
          kioskId: editingId,
          payload,
        }).unwrap();
        setEditingId(null);
      } else {
        await createKiosk(payload).unwrap();
      }
      setForm({
        deviceName: "",
        deviceKey: "",
        pairingCode: "",
        bridgeBaseUrl: "",
        signalrUrl: "",
        inactivitySeconds: 60,
        paymentsEnabled: true,
        printingEnabled: true,
        isActive: true,
      });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (kiosk) => {
    setEditingId(kiosk.kioskId);
    setForm({
      deviceName: kiosk.deviceName || "",
      deviceKey: kiosk.deviceKey || "",
      pairingCode: kiosk.pairingCode || "",
      bridgeBaseUrl: kiosk.bridgeBaseUrl || "",
      signalrUrl: kiosk.signalrUrl || "",
      inactivitySeconds: kiosk.inactivitySeconds || 60,
      paymentsEnabled:
        kiosk.paymentsEnabled !== undefined ? kiosk.paymentsEnabled : true,
      printingEnabled:
        kiosk.printingEnabled !== undefined ? kiosk.printingEnabled : true,
      isActive: kiosk.isActive !== undefined ? kiosk.isActive : true,
    });
    setShowForm(true);
  };

  const handleDelete = (kioskId) => {
    const kiosk = kiosks.find((k) => k.kioskId === kioskId);
    setDeleteModal({
      isOpen: true,
      kioskId,
      kioskName: kiosk?.deviceName || "this kiosk",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteKiosk(deleteModal.kioskId).unwrap();
      setDeleteModal({ isOpen: false, kioskId: null, kioskName: "" });
    } catch (err) {
      setDeleteModal({ isOpen: false, kioskId: null, kioskName: "" });
      const errorMessage =
        err?.data?.detail || err?.data?.message || "Failed to delete kiosk.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  // Lock/unlock action handlers are currently not required.
  // const handleLock = async (kioskId) => {
  //   try {
  //     await lockKiosk(kioskId).unwrap();
  //   } catch (err) {
  //     setErrorModal({
  //       isOpen: true,
  //       message: err?.data?.detail || "Failed to lock kiosk.",
  //     });
  //   }
  // };

  // const handleUnlock = async (kioskId) => {
  //   try {
  //     await unlockKiosk(kioskId).unwrap();
  //   } catch (err) {
  //     setErrorModal({
  //       isOpen: true,
  //       message: err?.data?.detail || "Failed to unlock kiosk.",
  //     });
  //   }
  // };

  const handleCancel = () => {
    setEditingId(null);
    setForm({
      deviceName: "",
      deviceKey: "",
      pairingCode: "",
      bridgeBaseUrl: "",
      signalrUrl: "",
      inactivitySeconds: 60,
      paymentsEnabled: true,
      printingEnabled: true,
      isActive: true,
    });
    setShowForm(false);
  };

  const formatLastSeen = (lastSeenAt) => {
    if (!lastSeenAt) return "Never";
    const date = new Date(lastSeenAt);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const columns = [
    { key: "deviceName", label: "Kiosk Name" },
    { key: "deviceKey", label: "Device Key" },
    { key: "pairingCode", label: "Pairing Code" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = kiosks.map((kiosk) => {
    const isLocked = kiosk.isLocked || false;

    return {
      id: kiosk.kioskId,
      deviceName: kiosk.deviceName,
      deviceKey: kiosk.deviceKey || "—",
      pairingCode: kiosk.pairingCode || "—",
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {isLocked ? "Locked" : kiosk.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <ActionButtons
          actions={[
            {
              icon: "edit",
              label: "Edit",
              color: "primary",
              onClick: () => handleEdit(kiosk),
            },
            // isLocked
            //   ? {
            //       icon: "unlock",
            //       label: "Unlock",
            //       color: "emerald",
            //       onClick: () => handleUnlock(kiosk.kioskId),
            //     }
            //   : {
            //       icon: "lock",
            //       label: "Lock",
            //       color: "amber",
            //       onClick: () => handleLock(kiosk.kioskId),
            //     },
            {
              icon: "delete",
              label: "Delete",
              color: "danger",
              onClick: () => handleDelete(kiosk.kioskId),
            },
          ]}
        />
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kiosks"
        subtitle="Manage self-service kiosks."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setShowForm(true)}
              disabled={isLimitReached}
            >
              Add Kiosk
            </Button>
          )
        }
      />
      {isLimitReached && (
        <Alert tone="warning">
          Kiosk limit reached for this store.
          {maxKiosks !== null && Number.isFinite(maxKiosks)
            ? ` Max kiosks: ${maxKiosks}.`
            : ""}
        </Alert>
      )}

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Kiosk" : "Add Kiosk"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Kiosk Name"
                name="deviceName"
                placeholder="Kiosk 01"
                value={form.deviceName}
                onChange={handleChange}
                required
              />
              <Input
                label="Device Key"
                name="deviceKey"
                placeholder="KIOSK-01"
                value={form.deviceKey}
                onChange={handleChange}
                required
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Pairing Code"
                name="pairingCode"
                placeholder="1111-1111"
                value={form.pairingCode}
                onChange={handleChange}
                required
              />
              <Input
                label="Inactivity Timeout (seconds)"
                name="inactivitySeconds"
                type="number"
                placeholder="60"
                value={form.inactivitySeconds}
                onChange={handleChange}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Bridge Base URL (Optional)"
                name="bridgeBaseUrl"
                placeholder="http://localhost:6001"
                value={form.bridgeBaseUrl}
                onChange={handleChange}
              />
              <Input
                label="SignalR URL (Optional)"
                name="signalrUrl"
                placeholder="/hubs/devices"
                value={form.signalrUrl}
                onChange={handleChange}
              />
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
                Features & Status
              </h3>
              <div className="grid gap-3 md:grid-cols-3">
                <Checkbox
                  label="Payments Enabled"
                  name="paymentsEnabled"
                  checked={form.paymentsEnabled}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Printing Enabled"
                  name="printingEnabled"
                  checked={form.printingEnabled}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Active"
                  name="isActive"
                  checked={form.isActive}
                  onChange={handleChange}
                />
              </div>
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Kiosk {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save kiosk."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Kiosk
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                All Kiosks
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {kiosks.length} kiosk{kiosks.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading kiosks...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load kiosks."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table columns={columns} rows={rows} emptyText="No kiosks yet." />
            )}
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, kioskId: null, kioskName: "" })
        }
        title="Delete Kiosk"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.kioskName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone.
        </p>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Kiosks;
